import DonorPaymentApi from "@/api/DonorPaymentApi";
import IResponseModel from "@/api/ResponseModel";
import ChargeDetail from "@/models/APIModels/ChargeDetail";
import DonorPayment from "@/models/APIModels/DonorPayment";
import {
  IncomingPaymentTransactionDetail,
  IncomingPaymentTransactionSummary,
} from "@/models/APIModels/IncomingPaymentTransactionList";
import DonorPaymentTableData from "@/models/DonorPaymentTableData";
import ChargeDetailsViewModel from "@/models/ViewModels/ChargeDetailsModel";
import DonorPaymentViewModel from "@/models/ViewModels/DonorPaymentModel";
import { formatDate } from "@/shared/helperFunctions";
import moment from "moment";

export default class DonorPaymentUseCase {
  private readonly _donorPaymentApi: DonorPaymentApi;
  constructor(donorPaymentApi: DonorPaymentApi = new DonorPaymentApi()) {
    this._donorPaymentApi = donorPaymentApi;
  }

  async getList(filter: {
    fromDate?: Date;
    toDate?: Date;
    donorName?: string;
    donorUserReference?: string;
    recognisedSchoolName?: string;
  }) {
    try {
      let queryStringArray = [];
      if (filter.fromDate) queryStringArray.push(`fromDate=${filter.fromDate}`);

      if (filter.toDate) queryStringArray.push(`toDate=${filter.toDate}`);

      if (filter.donorName)
        queryStringArray.push(
          `donorName=${encodeURIComponent(filter.donorName)}`,
        );

      if (filter.recognisedSchoolName)
        queryStringArray.push(
          `recognisedSchoolName=${encodeURIComponent(filter.recognisedSchoolName)}`,
        );
      if (filter.donorUserReference) {
        queryStringArray.push(
          `donorUserReference=${encodeURIComponent(filter.donorUserReference)}`,
        );
      }

      const result = await this._donorPaymentApi.getList(
        queryStringArray.join("&"),
      );
      if (result.content)
        result.content = result.content.map((item) =>
          this.mapDonorPaymentToViewModel(item),
        );

      return result;
    } catch (error) {
      throw error;
    }
  }

  async getTransactionsList(
    paymentStatusCds: string[],
    fromDt?: string,
    toDt?: string,
  ) {
    try {
      const queryString = [];
      if (fromDt)
        queryString.push(
          `fromDt=${moment(fromDt, "DD/MM/YYYY").format("YYYY-MM-DDTHH:mm:ss")}`,
        );
      if (toDt)
        queryString.push(
          `toDt=${moment(toDt, "DD/MM/YYYY").format("YYYY-MM-DDTHH:mm:ss")}`,
        );
      if (paymentStatusCds.length > 0) {
        queryString.push(
          `paymentStatusCds=${encodeURIComponent(paymentStatusCds.join(","))}`,
        );
      }
      const result = await this._donorPaymentApi.getTransactionsList(
        queryString.join("&"),
      );
      if (result.content) {
        result.content = result.content.map((item) =>
          this.mapTransactionListToViewModel(item),
        );
      }
      return result;
    } catch (error) {
      throw error;
    }
  }

  async donorPaymentTransactionsSummary() {
    try {
      const result = await this._donorPaymentApi.getTransactionsSummary();
      if (result.content)
        result.content = this.mapTransactionListSummaryToViewModel(
          result.content,
        );
      return result;
    } catch (error) {
      throw error;
    }
  }

  async getAllTableData() {
    try {
      const result = await this._donorPaymentApi.getList("");
      let data: IResponseModel<DonorPaymentTableData[]> = {
        ...result,
      } as IResponseModel<DonorPaymentTableData[]>;
      if (result.content)
        data.content = result.content.map((item) => this.mapToTableData(item));
      // data.content = [];
      return data;
    } catch (error) {
      throw error;
    }
  }

  async chargeAccount(userReference: string) {
    try {
      const result = await this._donorPaymentApi.chargeAccount(userReference);
      if (result.content) {
        result.content = this.mapChargeDetailtoViewModel(result.content);
      }
      return result;
    } catch (error) {
      throw error;
    }
  }

  async refundAccount(
    userReference: string,
    chargeReference: string,
    refundAmount: number,
    refundReason: string,
  ) {
    try {
      const queryString = `refundAmount=${encodeURIComponent(refundAmount)}&chargereference=${encodeURIComponent(chargeReference)}&refundreason=${encodeURIComponent(refundReason)}`;
      const result = await this._donorPaymentApi.refundAccount(
        userReference,
        queryString,
      );
      if (result.content) {
        result.content = this.mapChargeDetailtoViewModel(result.content);
      }
      return result;
    } catch (error) {
      throw error;
    }
  }

  async donorPaymentSummary(schoolReference?: string) {
    try {
      const queryString = schoolReference
        ? `schoolReference=${encodeURIComponent(schoolReference)}`
        : "";
      const result =
        await this._donorPaymentApi.donorPaymentSummary(queryString);
      return result;
    } catch (error) {
      throw error;
    }
  }

  private mapDonorPaymentFromViewModel(
    donorpaymentViewModel: DonorPaymentViewModel,
  ): DonorPayment {
    return { ...donorpaymentViewModel };
  }

  private mapDonorPaymentToViewModel(
    donorPayment: DonorPayment,
  ): DonorPaymentViewModel {
    return { ...donorPayment };
  }

  private mapToTableData(donorPayment: DonorPayment): DonorPaymentTableData {
    return {
      paymentAmount: `$ ${donorPayment.paymentAmt}`,
      paymentDate: formatDate(donorPayment.paymentDT),
      // paymentType: mapReferenceCodeToName(
      //   store.state.referenceData.paymentSourceTypes,
      //   donorPayment.donorPaymentType
      // ),
      // paymentStatus: mapReferenceCodeToName(
      //   store.state.referenceData.donorPaymentStatuses,
      //   donorPayment.donorPaymentStatusCd
      // ),
      reference: donorPayment.reference,
    };
  }

  private mapChargeDetailtoViewModel(
    chargeDetail: ChargeDetail,
  ): ChargeDetailsViewModel {
    return { ...chargeDetail };
  }

  private mapChargeDetailfromViewModel(
    chargeDetailViewModel: ChargeDetailsViewModel,
  ): ChargeDetail {
    return { ...chargeDetailViewModel };
  }

  private mapTransactionListToViewModel(
    transactionList: IncomingPaymentTransactionDetail,
  ): IncomingPaymentTransactionDetail {
    return { ...transactionList };
  }

  private mapTransactionListSummaryToViewModel(
    transactionList: IncomingPaymentTransactionSummary,
  ): IncomingPaymentTransactionSummary {
    return { ...transactionList };
  }
}
